import { Assignment, Folder, TrendingUp } from "@mui/icons-material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Alert, Box, CircularProgress, Divider, Grid2, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getManagedProcedures, getManagedProjects, getManagerCounters } from "../../api/app";
import { useTranslationHook } from "../../hooks";
import { TenderProcedure } from "../../types/model/procedure/TenderProcedure";
import { Project } from "../../types/model/project";
import { ManagerCounters } from "../../types/model/stats";
import ActivityWizardDialog from "../Activity/ActivityWizardDialog";
import { ProcedureList } from "../Procedure";
import ProcedureWizardDialog from "../Procedure/ProcedureWizardDialog";
import ProjectQuickCreateDialog from "../Projects/ProjectQuickCreateDialog";
import ProjectTable from "../Projects/ProjectTable";
import { SectionAccordion } from "./SectionAccordion";
import StatCard from "./StatCard";

const BuyerDashboard = () => {
  const { t } = useTranslationHook();
  const navigate = useNavigate();

  const [counters, setCounters] = useState<ManagerCounters>();
  const [countersLoading, setCountersLoading] = useState(false);

  const [procedures, setProcedures] = useState<TenderProcedure[]>([]);
  const [proceduresLoading, setProceduresLoading] = useState(false);

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const [proceduresExpanded, setProceduresExpanded] = useState(false);

  const [projectCreateOpen, setProjectCreateOpen] = useState(false);
  const [procedureWizardOpen, setProcedureWizardOpen] = useState(false);
  const [activityWizardOpen, setActivityWizardOpen] = useState(false);

  const fetchCounters = async () => {
    setCountersLoading(true);
    try {
      const data = await getManagerCounters();
      setCounters(data);
    } catch (error) {
      console.error(error);
    } finally {
      setCountersLoading(false);
    }
  };

  const fetchManagedProcedures = async () => {
    setProceduresLoading(true);
    try {
      const data = await getManagedProcedures();
      setProcedures(data);
    } catch (error) {
      console.error(error);
    } finally {
      setProceduresLoading(false);
    }
  };

  const fetchManagedProjects = async () => {
    setProjectsLoading(true);
    try {
      const data = await getManagedProjects();
      setProjects(data);
    } catch (error) {
      console.error(error);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchCounters();
    fetchManagedProcedures();
    fetchManagedProjects();
  }, []);

  const handleRequestRefreshProcedures = () => {
    fetchManagedProcedures();
  };

  const handleRequestRefreshProjects = () => {
    fetchManagedProjects();
  };

  const handleUserRowClickProcedure = (procedure: TenderProcedure) => {
    navigate(`/accounts/${procedure.accountId}/projects/${procedure.projectId}/activities/${procedure.activityId}/procedures/${procedure.id}`);
  };

  const handleUserRowClickProject = (project: Project) => {
    navigate(`/accounts/${project.accountId}/projects/${project.id}`);
  };

  let countersContent;
  if (countersLoading) {
    countersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (counters) {
    countersContent = (
      <Grid2
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(4) }}
      >
        <Grid2 key="counters-projects" size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<Folder />}
            title={t("generic.stats.projects")}
            total={counters.projects.total}
            active={counters.projects.active}
            closed={counters.projects.closed}
            onAdd={() => setProjectCreateOpen(true)}
            onView={() => navigate("/projects/list")}
            accentColor="#16a34a"
          />
        </Grid2>
        <Grid2 key="counters-activities" size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<TrendingUp />}
            title={t("generic.stats.activities")}
            total={counters.activities.total}
            active={counters.activities.active}
            closed={counters.activities.closed}
            onAdd={() => setActivityWizardOpen(true)}
            accentColor="#d97706"
          />
        </Grid2>
        <Grid2 key="counters-procedures" size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<Assignment />}
            title={t("generic.stats.procedures")}
            total={counters.procedures.total}
            active={counters.procedures.active}
            closed={counters.procedures.closed}
            onAdd={() => setProcedureWizardOpen(true)}
            accentColor="#7c3aed"
          />
        </Grid2>
      </Grid2>
    );
  } else {
    countersContent = (
      <Typography align="center" sx={{ mt: 2 }}>
        {t("generic.stats.no-data")}
      </Typography>
    );
  }

  let projectsContent;
  if (projectsLoading) {
    projectsContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (projects.length > 0) {
    projectsContent = (
      <SectionAccordion
        title={t("generic.stats.my-projects")}
        count={projects.length}
        expanded={projectsExpanded}
        onToggle={() => setProjectsExpanded((e) => !e)}
      >
        <ProjectTable
          tableData={projects}
          onRequestRefresh={handleRequestRefreshProjects}
          onRowClick={handleUserRowClickProject}
        />
      </SectionAccordion>
    );
  } else {
    projectsContent = (
      <Alert
        severity="info"
        icon={<WarningAmberRoundedIcon />}
        sx={{ borderRadius: 2 }}
      >
        {t("generic.stats.no-projects")}
      </Alert>
    );
  }

  let proceduresContent;
  if (proceduresLoading) {
    proceduresContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (procedures.length > 0) {
    proceduresContent = (
      <SectionAccordion
        title={t("generic.stats.my-procedures")}
        count={procedures.length}
        expanded={proceduresExpanded}
        onToggle={() => setProceduresExpanded((e) => !e)}
      >
        <ProcedureList
          procedures={procedures}
          loading={proceduresLoading}
          onRefresh={handleRequestRefreshProcedures}
          onProcedureSelected={(p) => handleUserRowClickProcedure(p as TenderProcedure)}
          readOnly={true}
        />
      </SectionAccordion>
    );
  } else {
    proceduresContent = (
      <Alert
        severity="info"
        icon={<WarningAmberRoundedIcon />}
        sx={{ borderRadius: 2 }}
      >
        {t("generic.stats.no-procedures")}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Counters */}
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: 2,
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t("generic.stats.overview")}
        </Typography>

        {countersContent}

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ my: 4 }}
        >
          <Divider sx={{ flexGrow: 1 }} />
          <Typography
            variant="overline"
            sx={{ color: "text.disabled", fontWeight: 600, letterSpacing: 1 }}
          >
            {t("generic.stats.operational-summary")}
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Stack>

        <Stack spacing={3}>
          {/* Managed Projects Table */}
          {projectsContent}

          {/* Managed Procedures Table */}
          {proceduresContent}
        </Stack>
      </Paper>

      {/* Project Creation Dialog */}
      <ProjectQuickCreateDialog
        open={projectCreateOpen}
        onClose={() => setProjectCreateOpen(false)}
        shouldNavigate
        onSuccess={() => {
          setProjectCreateOpen(false);
          fetchManagedProjects();
        }}
      />

      {/* Activity Wizard Dialog */}
      <ActivityWizardDialog
        open={activityWizardOpen}
        onClose={() => setActivityWizardOpen(false)}
        onSuccess={() => {
          setActivityWizardOpen(false);
          fetchCounters();
        }}
      />

      {/* Procedure Wizard Dialog */}
      <ProcedureWizardDialog
        open={procedureWizardOpen}
        onClose={() => setProcedureWizardOpen(false)}
        onSuccess={() => {
          setProcedureWizardOpen(false);
          fetchManagedProcedures();
        }}
      />
    </Box>
  );
};

export default BuyerDashboard;
